import {SingletonService} from '@/common/dependency.configs'
import {inject, injectable} from 'inversify'
import HttpHandler from '@/common/services/connect/HttpHandler'
import ResponseTypes from '@/common/enums/responseTypesEnum'
import SERVICE_PATH_CONSTANTS from '@/common/constant/servicePathConstants'

/**
 * Request/response DTO for the indicators endpoint (inside the RestResponse
 * envelope). `payload` is `null` until the row is created via POST.
 */
export interface ApplicantIndicatorDto {
  applicantForeignStatusIndicatorId?: string
  foreign?: boolean
  reportable?: boolean
}

/**
 * Patch body for create/update. Omitted fields are left untouched; `false`
 * resets a field.
 */
export type ApplicantIndicatorPatch = Pick<ApplicantIndicatorDto, 'foreign' | 'reportable'>

@SingletonService
@injectable()
class ApplicantIndicatorFactory {
  constructor(
    @inject(HttpHandler)
    private httpHandler: HttpHandler
  ) {}

  private getIndicatorsUrl(workspaceUUID: string, applicantId: number): string {
    return `${SERVICE_PATH_CONSTANTS.WORKSPACE_URL_TEMPLATE}/${workspaceUUID}/applicant/${applicantId}/indicators`
  }

  /**
   * Unwraps the RestResponse payload. Returns `null` when no row exists yet —
   * callers must null-check rather than assume an object shape.
   */
  private extractPayload(response: unknown): ApplicantIndicatorDto | null {
    if (response && typeof response === 'object' && 'payload' in response) {
      return (response as {payload: ApplicantIndicatorDto | null}).payload ?? null
    }
    return (response as ApplicantIndicatorDto | null) ?? null
  }

  /** Reads both indicators and the row id. Returns `null` if no row exists. */
  async getIndicators(workspaceUUID: string, applicantId: number): Promise<ApplicantIndicatorDto | null> {
    const response = await this.httpHandler.get(
      this.getIndicatorsUrl(workspaceUUID, applicantId),
      {},
      ResponseTypes.Data
    )
    return this.extractPayload(response)
  }

  /**
   * Creates the indicators row (first write only). Response includes the
   * generated id needed for later PUTs. A second POST returns 400 (exists).
   */
  async createIndicators(
    workspaceUUID: string,
    applicantId: number,
    patch: ApplicantIndicatorPatch
  ): Promise<ApplicantIndicatorDto | null> {
    const response = await this.httpHandler.post(
      this.getIndicatorsUrl(workspaceUUID, applicantId),
      patch,
      {},
      ResponseTypes.Data
    )
    return this.extractPayload(response)
  }

  /** Updates an existing row by id (patch semantics). Returns the full state. */
  async updateIndicators(
    workspaceUUID: string,
    applicantId: number,
    applicantForeignStatusIndicatorId: string,
    patch: ApplicantIndicatorPatch
  ): Promise<ApplicantIndicatorDto | null> {
    const response = await this.httpHandler.put(
      `${this.getIndicatorsUrl(workspaceUUID, applicantId)}/${applicantForeignStatusIndicatorId}`,
      patch,
      {},
      ResponseTypes.Data
    )
    return this.extractPayload(response)
  }
}

export default ApplicantIndicatorFactory

